import { jsx, Fragment } from "react/jsx-runtime";
import { FormField, Input, FormHelp, Box, Label, FieldSet, LabeledControl, Radio } from "@zillow/constellation";
import { forwardRef, useRef, useState, useImperativeHandle, useEffect, useCallback } from "react";
const UncontrolledInputField = forwardRef(function UncontrolledInputField2(props, ref) {
  const inputRef = useRef(null);
  const [error, setError] = useState(() => null);
  useImperativeHandle(ref, () => ({
    updateAndGetError: () => {
      if (!inputRef.current) {
        throw new Error("Input ref not available");
      }
      const nextError = props.getError(inputRef.current.value);
      setError(nextError || null);
      return nextError;
    },
    setError,
    setValue: (value) => {
      if (!inputRef.current) {
        throw new Error("Input ref not available");
      }
      inputRef.current.value = value;
    },
    focus: () => {
      if (!inputRef.current) {
        throw new Error("Input ref not available");
      }
      inputRef.current.focus();
      let focusInterval;
      const focusAndCheck = () => {
        if (!inputRef.current) {
          throw new Error("Input ref not available");
        }
        inputRef.current.focus();
        if (inputRef.current === document.activeElement) {
          window.clearInterval(focusInterval);
          return true;
        }
        return false;
      };
      if (!focusAndCheck()) {
        focusInterval = window.setInterval(focusAndCheck, 10);
      }
    }
  }));
  useEffect(() => {
    var _a;
    (_a = props.onMount) == null ? void 0 : _a.call(props, inputRef);
  }, []);
  const valueOnFocus = useRef("");
  return /* @__PURE__ */ jsx(FormField, { ...props.formFieldProps, label: /* @__PURE__ */ jsx(Box, { as: Label, sx: {
    "&&&&": {
      display: "none"
    }
  }, children: props.placeholder }), description: /* @__PURE__ */ jsx(FormHelp, { error: !!error, children: error || /* @__PURE__ */ jsx(Fragment, { children: " " }) }), control: /* @__PURE__ */ jsx(Input, { autoComplete: props.autoComplete, autoFocus: props.autoFocus, defaultValue: props.defaultValue, error, name: props.name, onFocus: () => {
    valueOnFocus.current = inputRef.current.value;
  }, onBlur: () => {
    var _a;
    setErrorIfNotFocused(setError, props.getError);
    if (inputRef.current.value !== valueOnFocus.current) {
      (_a = props.onBlurAfterChange) == null ? void 0 : _a.call(props, inputRef.current.value);
    }
  }, onChange: (e) => {
    var _a;
    (_a = props.onChange) == null ? void 0 : _a.call(props, e.target.value);
    setErrorIfNotFocused(setError, props.getError)(e);
  }, placeholder: props.placeholder, ref: inputRef, type: props.type }) });
});
const UncontrolledFieldSet = forwardRef(function UncontrolledFieldSet2(props, ref) {
  const inputRefs = useRef([]);
  const [error, setError] = useState(() => null);
  useImperativeHandle(ref, () => ({
    updateAndGetError: () => {
      var _a;
      const value = (_a = inputRefs.current.find((ref2) => ref2.checked)) == null ? void 0 : _a.value;
      const nextError = props.getError(value);
      setError(nextError || null);
      return nextError;
    },
    setError
  }));
  const addInputRef = useCallback((el) => {
    if (!el) {
      inputRefs.current = inputRefs.current.filter((ref2) => ref2 !== el);
      return;
    }
    if (!inputRefs.current.includes(el)) {
      inputRefs.current.push(el);
    }
  }, []);
  return /* @__PURE__ */ jsx(FieldSet, { description: /* @__PURE__ */ jsx(FormHelp, { error: !!error, children: error || /* @__PURE__ */ jsx(Fragment, { children: " " }) }), onChange: (e) => setError(props.getError(e.target.value) || null), children: props.options.map((option) => /* @__PURE__ */ jsx(LabeledControl, { disabled: props.disabled, label: /* @__PURE__ */ jsx(Label, { children: option.label }), control: /* @__PURE__ */ jsx(Radio, { name: props.name, value: option.value, onChange: (e) => {
    var _a;
    return (_a = props == null ? void 0 : props.onChange) == null ? void 0 : _a.call(props, e.target.value);
  }, ref: addInputRef }) }, option.value)) });
});
function setErrorIfNotFocused(setError, getError) {
  return (e) => {
    if (document.activeElement === e.target) {
      setError(null);
      return;
    }
    setError(getError(e.target.value) || null);
  };
}
export {
  UncontrolledFieldSet,
  UncontrolledInputField
};
